import { Injectable, OnDestroy } from '@angular/core';
import { IFillSiteWithFiles } from '@features/admin/pages/site-detail/@shared/models/source-site-with-files.interface';
import {
  AssignSourceSitesToFillSiteCommand,
  BatchQueryResultViewModel,
  CreateFillSiteCommand,
  EditFillSiteCommand,
  FillSiteQPSearchViewModelTotalTypePagedResultResponse,
  FillSiteSearchViewModelSiteTotalTypePagedResultResponse,
  FillSiteSourceSiteSearchViewModelFillSiteSourceSiteTotalTypePagedResultResponse,
  FillSiteSourceSiteTotalType,
  FillSiteUserSearchViewModelTotalTypePagedResultResponse,
  FillSiteViewModel,
  IQualifiedPersonViewModel,
  ISourceSiteViewModel,
  IUserSearchViewModel,
  SiteTotalType,
  TotalType
} from '@services/cleanfill-api/cleanfill-api.generated';
import { HttpFillSitesService } from '@services/cleanfill-core/http-fill-sites/http-fill-sites.service';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { SearchQuery } from '@view-models/search-query/search-query';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StoreFillSitesService implements OnDestroy {
  private fillSiteSubj$ = new BehaviorSubject<FillSiteViewModel>(null);
  fillSite$ = this.fillSiteSubj$.asObservable();
  fillSiteWithFiles$: Observable<IFillSiteWithFiles> = this.fillSite$.pipe(
    filter(Boolean),
    switchMap((site: ISourceSiteViewModel) =>
      this.httpFillSites.getAttachments(site.uniqueId).pipe(map((attachments) => ({ ...site, files: attachments })))
    )
  );

  private usersPagedResultSubj$ = new BehaviorSubject<FillSiteUserSearchViewModelTotalTypePagedResultResponse>(null);
  usersPagedResult$ = this.usersPagedResultSubj$.asObservable();
  users$ = this.usersPagedResult$.pipe(map((result) => result?.records || []));
  totalUsers$ = this.usersPagedResult$.pipe(map((v) => v?.totals?.find((total) => total.type === TotalType.Total)?.value || 0));

  private fillSitePagedResultSubj$ = new BehaviorSubject<FillSiteSearchViewModelSiteTotalTypePagedResultResponse>(null);
  fillSitePagedResult$ = this.fillSitePagedResultSubj$.asObservable();
  fillSites$ = this.fillSitePagedResultSubj$.pipe(map((result) => result?.records || []));
  totalFillSites$ = this.fillSitePagedResult$.pipe(
    map((v) => v?.totals?.find((total) => total.type === (SiteTotalType.AllStatus as string))?.value || 0)
  );

  private invitedFillSiteClientsPagedResultSubj$ =
    new BehaviorSubject<FillSiteSourceSiteSearchViewModelFillSiteSourceSiteTotalTypePagedResultResponse>(null);
  invitedFillSiteClientsPagedResult$ = this.invitedFillSiteClientsPagedResultSubj$.asObservable();
  invitedFillSiteClients$ = this.invitedFillSiteClientsPagedResult$.pipe(map((result) => result?.records || []));
  totalTnvitedFillSiteClients$ = this.invitedFillSiteClientsPagedResult$.pipe(
    map((v) => v?.totals?.find((total) => total.type === (FillSiteSourceSiteTotalType.All as string))?.value || 0)
  );

  private fillSiteQpsSubj$ = new BehaviorSubject<IQualifiedPersonViewModel[]>([]);
  fillSiteQps$ = this.fillSiteQpsSubj$.asObservable();

  private fillSiteBatchesSubj$ = new BehaviorSubject<BatchQueryResultViewModel[]>([]);
  fillSiteBatches$ = this.fillSiteBatchesSubj$.asObservable();

  private assignedQpsPagedResultSubj$ = new BehaviorSubject<FillSiteQPSearchViewModelTotalTypePagedResultResponse>(null);
  assignedQpsPagedResult$ = this.assignedQpsPagedResultSubj$.asObservable();
  assignedQps$ = this.assignedQpsPagedResult$.pipe(map((result) => result?.records || []));
  totalAssignedQps$ = this.assignedQpsPagedResult$.pipe(map((v) => v?.totals?.find((total) => total.type === TotalType.Total)?.value || 0));

  private createdFillSiteSubj$ = new BehaviorSubject<FillSiteViewModel>(null);
  createdFillSite$ = this.createdFillSiteSubj$.asObservable();
  createdFillSiteUniqueId$ = this.createdFillSite$.pipe(map((fillSite) => fillSite?.uniqueId));

  subscriptions = new Subscription();

  constructor(private httpFillSites: HttpFillSitesService) { }

  loadFillSites(query: ISearchQuery): void {
    const sub = this.httpFillSites.getFillSites(query).subscribe((res) => this.fillSitePagedResultSubj$.next(res));
    this.subscriptions.add(sub);
  }
  loadAllFillSites(query: ISearchQuery): void {
    const sub = this.httpFillSites.getALLFillSites(query).subscribe((result) => this.fillSitePagedResultSubj$.next(result));
    this.subscriptions.add(sub);
  }
  loadFillSitesById(uniqueId: string, query: ISearchQuery): void {
    const sub = this.httpFillSites.getFillSitesById(uniqueId, query).subscribe((res) => this.fillSitePagedResultSubj$.next(res));
    this.subscriptions.add(sub);
  }

  loadFillSitesBySourceSite(uniqueId: string, query: ISearchQuery) {
    const sub = this.httpFillSites.getFillSitesBySourceSite(uniqueId, query).subscribe((res) => this.fillSitePagedResultSubj$.next(res));
    this.subscriptions.add(sub);
  }
  loadFillSiteSourceSites(uniqueId: string, query: SearchQuery) {
    const sub = this.httpFillSites
      .getFillSiteSourceSites(uniqueId, query)
      .subscribe((res) => this.invitedFillSiteClientsPagedResultSubj$.next(res));
    this.subscriptions.add(sub);
  }

  loadFillSiteQps(fillSiteUniqueId: string): void {
    const sub = this.httpFillSites.getFillSiteQps(fillSiteUniqueId).subscribe((res) => {
      this.fillSiteQpsSubj$.next(res || []);
    });
    this.subscriptions.add(sub);
  }

  loadBatches(fillSiteUniqueId: string) {
    this.httpFillSites
      .getBatches(fillSiteUniqueId)
      .subscribe((result: BatchQueryResultViewModel[]) => this.fillSiteBatchesSubj$.next(result));
  }

  createFillSite(payload: CreateFillSiteCommand, fn = () => { }) {
    const sub = this.httpFillSites.createFillSite(payload).subscribe((fillSite) => {
      this.createdFillSiteSubj$.next(fillSite);
      fn();
    });
    this.subscriptions.add(sub);
  }

  editFillSite(payload: EditFillSiteCommand, fn = () => { }) {
    const sub = this.httpFillSites.editFillSite(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }

  clearNewlyCreatedFillSite() {
    this.createdFillSiteSubj$.next(undefined);
  }
  clearFillSites() {
    this.fillSitePagedResultSubj$.next(undefined);
  }

  loadFillSite(fillSiteUniqueId: string) {
    const sub = this.httpFillSites.getFillSite(fillSiteUniqueId).subscribe((site) => this.fillSiteSubj$.next(site));
    this.subscriptions.add(sub);
  }
  getFillSite(fillSiteUniqueId: string) {
    return this.httpFillSites.getFillSite(fillSiteUniqueId).pipe(
      filter(Boolean),
      switchMap((site: ISourceSiteViewModel) =>
        this.httpFillSites.getAttachments(site.uniqueId).pipe(map((attachments) => ({ ...site, files: attachments })))
      )
    );
  }
  loadUsers(fillSiteUniqueId: string, query: SearchQuery) {
    const sub = this.httpFillSites.getUsers(fillSiteUniqueId, query).subscribe((result) => this.usersPagedResultSubj$.next(result));
    this.subscriptions.add(sub);
  }

  loadAssignedQps(fillSiteUniqueId: string, query: SearchQuery) {
    const sub = this.httpFillSites
      .getAssignedQualifiedPersons(fillSiteUniqueId, query)
      .subscribe((result) => this.assignedQpsPagedResultSubj$.next(result));
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  assignUser(fillSiteUniqueId: string, user: IUserSearchViewModel, fn = () => { }): void {
    const sub = this.httpFillSites.assignUser(fillSiteUniqueId, user).subscribe(fn);
    this.subscriptions.add(sub);
  }

  unassignUser(uniqueId: string, fn = () => { }) {
    const sub = this.httpFillSites.unassignUser(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  assignQp(fillSiteUniqueId: string, qpUniqueId: string, fn = () => { }) {
    const sub = this.httpFillSites.assignQp(fillSiteUniqueId, qpUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }
  inviteSourceSitesToFillSite(payload: AssignSourceSitesToFillSiteCommand): Observable<void> {
    return this.httpFillSites.assignSourceSitesToFillSite(payload);
  }

  unassignQp(qpUniqueId: string, fn = () => { }) {
    const sub = this.httpFillSites.unassignQp(qpUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  deleteFillSite(uniqueId: string, fn: () => void) {
    const sub = this.httpFillSites.deleteFillSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  archiveFillSite(uniqueId: string, fn: () => void) {
    const sub = this.httpFillSites.archiveFillSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  reactivateFillSite(uniqueId: string, fn: () => void) {
    const sub = this.httpFillSites.reactivateFillSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }
}
